import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { 
    Table, 
    Card, 
    Button, 
    Input, 
    Space, 
    Tag, 
    Modal, 
    Form, 
    InputNumber, 
    Select, 
    Typography, 
    message, 
    Popconfirm,
    Badge,
    Tooltip,
    Tabs
} from 'antd';
import { 
    SearchOutlined, 
    EditOutlined, 
    DeleteOutlined, 
    PlusOutlined,
    SettingOutlined,
    SyncOutlined
} from '@ant-design/icons';
import { useAdmin } from '../../hooks/useAdmin';
import { useTranslation } from 'react-i18next';

const { Title, Text } = Typography;
const { Option } = Select;

const SystemSettings = () => {
    const { t } = useTranslation();
    const { loading, getSystemSettings, createSystemSetting, updateSystemSettings, deleteSystemSetting } = useAdmin();
    const [settings, setSettings] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingSetting, setEditingSetting] = useState(null);
    const [form] = Form.useForm();
    const [activeTab, setActiveTab] = useState('all');

    const loadSettings = useCallback(async () => {
        const result = await getSystemSettings();
        if (result.success) {
            setSettings(result.data);
        }
    }, [getSystemSettings]);

    useEffect(() => {
        loadSettings();
    }, [loadSettings]);

    const handleAdd = () => {
        setEditingSetting(null);
        form.resetFields();
        setIsModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditingSetting(record);
        form.setFieldsValue({
            ...record,
            setting_value: record.setting_type === 'number' ? parseFloat(record.setting_value) : record.setting_value
        });
        setIsModalVisible(true);
    };

    const handleDelete = async (id) => {
        const result = await deleteSystemSetting(id);
        if (result.success) {
            message.success(t('admin.settings.delete_success') || 'Supprimé avec succès');
            loadSettings();
        }
    };

    const handleModalOk = async () => {
        try {
            const values = await form.validateFields();
            let result;
            
            // Format values for backend
            const payload = {
                ...values,
                setting_value: values.setting_value.toString()
            };

            if (editingSetting) {
                // Currently updateSystemSettings is bulk, but I'll use it for single for now or update it
                const bulkPayload = { [editingSetting.setting_key]: payload.setting_value };
                result = await updateSystemSettings(bulkPayload);
            } else {
                result = await createSystemSetting(payload);
            }

            if (result.success) {
                message.success(editingSetting ? (t('admin.settings.update_success') || 'Mis à jour') : (t('admin.settings.create_success') || 'Créé'));
                setIsModalVisible(false);
                loadSettings();
            }
        } catch (error) {
            console.error('Validate Failed:', error);
        }
    };

    const categories = useMemo(() => {
        const cats = [...new Set(settings.map(s => s.category))].filter(Boolean);
        return ['all', ...cats];
    }, [settings]);

    const filteredData = useMemo(() => {
        return settings.filter(item => {
            const matchesSearch = item.setting_key.toLowerCase().includes(searchText.toLowerCase()) || 
                                 (item.description && item.description.toLowerCase().includes(searchText.toLowerCase()));
            const matchesTab = activeTab === 'all' || item.category === activeTab;
            return matchesSearch && matchesTab;
        });
    }, [settings, searchText, activeTab]);

    const columns = [
        {
            title: t('admin.settings.key') || 'Clé',
            dataIndex: 'setting_key',
            key: 'setting_key',
            render: (text) => <Text strong style={{ color: '#6366f1' }}>{text}</Text>,
            sorter: (a, b) => a.setting_key.localeCompare(b.setting_key),
        },
        {
            title: t('admin.settings.value') || 'Valeur',
            dataIndex: 'setting_value',
            key: 'setting_value',
            render: (text, record) => {
                if (record.setting_type === 'boolean') {
                    return <Badge status={text === 'true' ? 'success' : 'error'} text={text === 'true' ? 'Activé' : 'Désactivé'} />;
                }
                return <Text code>{text}</Text>;
            }
        },
        {
            title: t('admin.settings.type') || 'Type',
            dataIndex: 'setting_type',
            key: 'setting_type',
            render: (type) => <Tag color="blue">{type.toUpperCase()}</Tag>,
            filters: [
                { text: 'String', value: 'string' },
                { text: 'Number', value: 'number' },
                { text: 'Boolean', value: 'boolean' },
                { text: 'JSON', value: 'json' },
            ],
            onFilter: (value, record) => record.setting_type === value,
        },
        {
            title: t('admin.settings.category') || 'Catégorie',
            dataIndex: 'category',
            key: 'category',
            render: (cat) => <Tag color="purple">{cat?.toUpperCase() || 'GENERAL'}</Tag>,
        },
        {
            title: t('admin.settings.description') || 'Description',
            dataIndex: 'description',
            key: 'description',
            ellipsis: true,
        },
        {
            title: t('common.actions') || 'Actions',
            key: 'actions',
            width: 120,
            render: (_, record) => (
                <Space>
                    <Tooltip title={t('common.edit') || 'Modifier'}>
                        <Button 
                            type="text" 
                            icon={<EditOutlined />} 
                            onClick={() => handleEdit(record)}
                            style={{ color: '#6366f1' }}
                        />
                    </Tooltip>
                    <Popconfirm
                        title={t('common.are_you_sure') || 'Êtes-vous sûr ?'}
                        onConfirm={() => handleDelete(record.id)}
                        okText={t('common.yes') || 'Oui'}
                        cancelText={t('common.no') || 'Non'}
                    >
                        <Tooltip title={t('common.delete') || 'Supprimer'}>
                            <Button type="text" danger icon={<DeleteOutlined />} />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div className="admin-settings-inner">
            <div className="page-title-section" style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <Title level={2} style={{ margin: 0, color: '#f1f5f9' }}>
                        <SettingOutlined style={{ marginRight: 12, color: '#6366f1' }} />
                        {t('admin.settings.title') || 'Paramètres Système'}
                    </Title>
                    <Text type="secondary">{t('admin.settings.subtitle') || 'Configurez les variables globales de la plateforme'}</Text>
                </div>
                <Space>
                    <Button 
                        icon={<SyncOutlined spin={loading} />} 
                        onClick={loadSettings}
                    >
                        {t('common.refresh') || 'Actualiser'}
                    </Button>
                    <Button 
                        type="primary" 
                        icon={<PlusOutlined />} 
                        onClick={handleAdd}
                        style={{ borderRadius: 8, height: 40, background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)', border: 'none' }}
                    >
                        {t('admin.settings.add_new') || 'Ajouter'}
                    </Button>
                </Space>
            </div>

            <Card className="glass-card" style={{ background: 'rgba(30, 41, 59, 0.4)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.05)' }}>
                <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Tabs 
                        activeKey={activeTab} 
                        onChange={setActiveTab} 
                        style={{ width: '70%' }}
                        items={categories.map(cat => ({
                            key: cat,
                            label: cat.toUpperCase(),
                        }))}
                    />
                    <Input
                        placeholder={t('admin.settings.search_placeholder') || 'Rechercher une clé...'}
                        prefix={<SearchOutlined />}
                        style={{ width: 250, borderRadius: 8 }}
                        onChange={e => setSearchText(e.target.value)}
                        allowClear
                    />
                </div>

                <Table 
                    columns={columns} 
                    dataSource={filteredData} 
                    rowKey="id"
                    loading={loading}
                    pagination={{ pageSize: 10, showSizeChanger: true }}
                    className="admin-table"
                />
            </Card>

            <Modal
                title={editingSetting ? (t('admin.settings.edit_title') || 'Modifier le paramètre') : (t('admin.settings.add_title') || 'Ajouter un paramètre')}
                open={isModalVisible}
                onOk={handleModalOk}
                onCancel={() => setIsModalVisible(false)}
                okText={t('common.save') || 'Enregistrer'}
                cancelText={t('common.cancel') || 'Annuler'}
                width={600}
                centered
                bodyStyle={{ paddingTop: 20 }}
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        name="setting_key"
                        label={t('admin.settings.key') || 'Clé'}
                        rules={[{ required: true, message: t('admin.settings.key_required') || 'Clé requise' }]}
                    >
                        <Input disabled={!!editingSetting} placeholder="e.g., withdrawal_fee_percent" />
                    </Form.Item>

                    <Form.Item
                        name="setting_type"
                        label={t('admin.settings.type') || 'Type'}
                        rules={[{ required: true }]}
                        initialValue="string"
                    >
                        <Select>
                            <Option value="string">String</Option>
                            <Option value="number">Number</Option>
                            <Option value="boolean">Boolean</Option>
                            <Option value="json">JSON</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="category"
                        label={t('admin.settings.category') || 'Catégorie'}
                        rules={[{ required: true }]}
                        initialValue="general"
                    >
                        <Select>
                            <Option value="general">General</Option>
                            <Option value="trade">Trade</Option>
                            <Option value="investment">Investment</Option>
                            <Option value="mlm">MLM</Option>
                            <Option value="security">Security</Option>
                            <Option value="api">API</Option>
                            <Option value="email">Email</Option>
                        </Select>

                    </Form.Item>

                    <Form.Item
                        noStyle
                        shouldUpdate={(prevValues, currentValues) => prevValues.setting_type !== currentValues.setting_type}
                    >
                        {({ getFieldValue }) => {
                            const type = getFieldValue('setting_type');
                            if (type === 'number') {
                                return (
                                    <Form.Item name="setting_value" label={t('admin.settings.value') || 'Valeur'} rules={[{ required: true }]}>
                                        <InputNumber style={{ width: '100%' }} />
                                    </Form.Item>
                                );
                            }
                            if (type === 'boolean') {
                                return (
                                    <Form.Item name="setting_value" label={t('admin.settings.value') || 'Valeur'} rules={[{ required: true }]}>
                                        <Select>
                                            <Option value="true">True</Option>
                                            <Option value="false">False</Option>
                                        </Select>
                                    </Form.Item>
                                );
                            }
                            return (
                                <Form.Item name="setting_value" label={t('admin.settings.value') || 'Valeur'} rules={[{ required: true }]}>
                                    <Input.TextArea rows={2} />
                                </Form.Item>
                            );
                        }}
                    </Form.Item>

                    <Form.Item
                        name="description"
                        label={t('admin.settings.description') || 'Description'}
                    >
                        <Input.TextArea rows={3} placeholder={t('admin.settings.description_placeholder') || 'Expliquez l\'usage de ce paramètre...'} />
                    </Form.Item>
                </Form>
            </Modal>

            <style dangerouslySetInnerHTML={{ __html: `
                .admin-page-container {
                    animation: fadeIn 0.5s ease-out;
                }
                .glass-card {
                    box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.37);
                }
                .admin-table .ant-table {
                    background: transparent !important;
                }
                .admin-table .ant-table-thead > tr > th {
                    background: rgba(15, 23, 42, 0.8) !important;
                    border-bottom: 1px solid rgba(255,255,255,0.05) !important;
                }
                .admin-table .ant-table-tbody > tr > td {
                    border-bottom: 1px solid rgba(255,255,255,0.02) !important;
                }
                .admin-table .ant-table-tbody > tr:hover > td {
                    background: rgba(99, 102, 241, 0.05) !important;
                }
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}} />
        </div>
    );
};

export default SystemSettings;
